import { A, D } from '@mobily/ts-belt';
import {
  Divider,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { countryDataByCode, SupportedCountryCodes } from '@Common/countries/country_data';

const sortedCountryCodes = A.sort(D.keys(countryDataByCode), (a, b) =>
  countryDataByCode[a].name.localeCompare(countryDataByCode[b].name),
);

export function parsePhoneNumber(
  fullNumber: string,
  defaultCountry: SupportedCountryCodes = 'FR',
): { localNumber: string; countryCode: SupportedCountryCodes } {
  if (!fullNumber.startsWith('+')) {
    return { localNumber: fullNumber, countryCode: defaultCountry };
  }

  // Match the longest dial code first
  let bestMatch: { code: SupportedCountryCodes; dialCode: string } | null = null;
  for (const [code, data] of Object.entries(countryDataByCode)) {
    if (
      fullNumber.startsWith(data.dialCode) &&
      (!bestMatch || data.dialCode.length > bestMatch.dialCode.length)
    ) {
      bestMatch = { code: code as SupportedCountryCodes, dialCode: data.dialCode };
    }
  }

  if (bestMatch) {
    return {
      localNumber: fullNumber.slice(bestMatch.dialCode.length),
      countryCode: bestMatch.code,
    };
  }

  return { localNumber: fullNumber, countryCode: defaultCountry };
}

export type PhoneNumberWithDialCodeInputProps = Omit<
  TextFieldProps,
  'onChange' | 'value' | 'data-testid'
> & {
  /** Full E.164 phone number (e.g. "+33612345678"). Used to pre-fill and detect the country. */
  value?: string;
  /** Default country code shown when no value is provided. Defaults to 'FR'. */
  defaultCountry?: SupportedCountryCodes;
  /** Called whenever the composed full phone number (dialCode + localDigits) changes. */
  onChange?: (fullPhoneNumber: string) => void;
  /** Called when the field loses focus. */
  onBlur?: () => void;
  testId?: string;
};

export function PhoneNumberWithDialCodeInput({
  value,
  defaultCountry = 'FR',
  onChange,
  onBlur,
  disabled,
  error,
  helperText,
  label,
  testId,
  ...rest
}: PhoneNumberWithDialCodeInputProps) {
  const [selectedCountry, setSelectedCountry] = useState<SupportedCountryCodes>(defaultCountry);
  const dialCode = useMemo(() => countryDataByCode[selectedCountry].dialCode, [selectedCountry]);

  const [localNumber, setLocalNumber] = useState('');

  useEffect(() => {
    if (value) {
      const parsed = parsePhoneNumber(value, defaultCountry);
      setSelectedCountry(parsed.countryCode);
      setLocalNumber(parsed.localNumber);
    } else {
      setSelectedCountry(defaultCountry);
      setLocalNumber('');
    }
  }, [value, defaultCountry]);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const debouncedOnChange = useMemo(
    () =>
      debounce((fullPhoneNumber: string) => {
        onChangeRef.current?.(fullPhoneNumber);
      }, 700),
    [],
  );

  useEffect(() => () => debouncedOnChange.cancel(), [debouncedOnChange]);

  const handleCountryChange = useCallback(
    (event: SelectChangeEvent) => {
      const code = event.target.value as SupportedCountryCodes;
      setSelectedCountry(code);
      const newDialCode = countryDataByCode[code].dialCode;
      setLocalNumber((prev) => {
        if (prev !== '') {
          debouncedOnChange.cancel();
          onChangeRef.current?.(newDialCode + prev);
        }
        return prev;
      });
    },
    [debouncedOnChange],
  );

  const handleLocalNumberChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newLocal = event.target.value;
      setLocalNumber(newLocal);
      if (newLocal !== '') {
        debouncedOnChange(dialCode + newLocal);
      } else {
        debouncedOnChange.cancel();
        onChangeRef.current?.('');
      }
    },
    [dialCode, debouncedOnChange],
  );

  const selected = countryDataByCode[selectedCountry];

  return (
    <TextField
      {...rest}
      type="tel"
      label={label}
      variant="outlined"
      value={localNumber}
      disabled={disabled}
      error={error}
      helperText={helperText}
      onChange={handleLocalNumberChange}
      onBlur={() => {
        debouncedOnChange.flush();
        onBlur?.();
      }}
      data-testid={testId}
      InputLabelProps={{ shrink: true }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start" sx={{ mr: 0 }}>
            <Select
              value={selectedCountry}
              onChange={handleCountryChange}
              disabled={disabled}
              variant="standard"
              disableUnderline
              data-testid={testId ? `${testId}_CountrySelect` : undefined}
              renderValue={() => (
                <Stack direction="row" alignItems="center" gap={0.5}>
                  <Typography component="span" fontSize="1.25rem" lineHeight={1}>
                    {selected.emoji}
                  </Typography>
                  <Typography component="span" noWrap>
                    {selected.dialCode}
                  </Typography>
                </Stack>
              )}
              sx={{ minWidth: 80 }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}>
              {sortedCountryCodes.map((code) => {
                const country = countryDataByCode[code];
                return (
                  <MenuItem
                    key={code}
                    value={code}
                    data-testid={testId ? `${testId}_CountryOption` : undefined}>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Typography component="span" fontSize="1.1rem">
                        {country.emoji}
                      </Typography>
                      <Typography component="span" variant="body2">
                        {country.name}
                      </Typography>
                      <Typography component="span" variant="body2" color="text.secondary">
                        {country.dialCode}
                      </Typography>
                    </Stack>
                  </MenuItem>
                );
              })}
            </Select>
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          </InputAdornment>
        ),
      }}
    />
  );
}
